import React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Shield, ShieldCheck, ShieldOff, Users, Building2, LogOut, GitBranch, Coins, Activity, TrendingUp, Cpu, Clock, Mic } from 'lucide-react';

export const Layout: React.FC = () => {
  const location = useLocation();
  const { logout } = useAdminAuth();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 shadow-sm">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center space-x-3 p-6 border-b border-gray-200">
            <Shield className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-gray-900 font-bold text-lg">SuprAgent</h1>
              <p className="text-gray-500 text-xs">Admin Portal</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            <Link
              to="/trial-requests"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/trial-requests')
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">Trial Requests</span>
            </Link>

            <Link
              to="/organizations"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/organizations') || location.pathname.startsWith('/organizations/')
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Building2 className="w-5 h-5" />
              <span className="font-medium">Organizations</span>
            </Link>

            <Link
              to="/workflow-migration"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/workflow-migration')
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <GitBranch className="w-5 h-5" />
              <span className="font-medium">Agent Migration</span>
            </Link>

            <Link
              to="/crons"
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                isActive('/crons')
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Clock className="w-5 h-5" />
              <span className="font-medium">Crons</span>
            </Link>

            <Link
              to="/platform-admins"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/platform-admins')
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <ShieldCheck className="w-5 h-5" />
              <span className="font-medium">Platform administrators</span>
            </Link>

            <Link
              to="/voice-evals"
              title="Speech-to-text benchmarks"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                isActive('/voice-evals')
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Mic className="w-5 h-5" />
              <span className="font-medium">Voice Evals</span>
            </Link>

            <div className="pt-4 mt-4 border-t border-gray-200">
              <p className="px-4 text-xs uppercase text-gray-400 font-semibold tracking-wider mb-2">Credits &amp; cost</p>
              <Link
                to="/pricing"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/pricing')
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {/* 28 Sep (mig 0702): one rate card, set per organisation — no money. */}
                <Coins className="w-5 h-5" />
                <span className="font-medium">Rate card</span>
              </Link>
              <Link
                to="/plans"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/plans')
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {/* 28 Sep: plans and money rates are retired; only the blocked signup domains remain. */}
                <ShieldOff className="w-5 h-5" />
                <span className="font-medium">Signup domains</span>
              </Link>
              <Link
                to="/llm-pricing"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/llm-pricing')
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Cpu className="w-5 h-5" />
                <span className="font-medium">Model pricing</span>
              </Link>
              <Link
                to="/usage"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/usage')
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Activity className="w-5 h-5" />
                <span className="font-medium">Usage & Cost</span>
              </Link>
              <Link
                to="/activity"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/activity')
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <TrendingUp className="w-5 h-5" />
                <span className="font-medium">Activity</span>
              </Link>
            </div>
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors w-full"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64">
        <div className="p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

